import { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Paper,
  Box,
  Typography,
  TextField,
  Button,
  Link,
  Alert,
  MenuItem,
  Grid,
  FormControlLabel,
  Switch,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  Checkbox,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'react-toastify';

import { useAuthStore } from '@store/authStore';
import authService from '@services/authService';
import { GenderType } from '@types/index';

// Validation schema with conditional validation
const registerSchema = z.object({
  is_below_18: z.boolean(),
  email: z.string().email('Invalid email address').optional().or(z.literal('')),
  password: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, 'Password must contain uppercase, lowercase, and number'),
  first_name: z.string().min(2, 'Name is required'),
  last_name: z.string().optional(),
  date_of_birth: z.string().min(1, 'Date of birth is required'),
  gender: z.nativeEnum(GenderType),
  phone: z.string().optional(),
  // Parent / guardian fields (required if is_below_18)
  parent_email: z.string().email('Invalid parent email address').optional().or(z.literal('')),
  parent_phone: z.string().optional(),
  parent_country: z.string().optional(),
  father_name: z.string().optional(),
  mother_name: z.string().optional(),
  guardian_name: z.string().optional(),
  // Consent fields
  consent_terms: z.boolean(),
  consent_data_processing: z.boolean(),
  consent_marketing: z.boolean().optional(),
}).refine((data) => {
  if (data.is_below_18) {
    return data.parent_email && data.parent_phone && data.parent_country && data.father_name && data.mother_name;
  }
  return !!data.email;
}, {
  message: 'Please fill in all required fields',
  path: ['parent_email'],
}).refine((data) => data.consent_terms === true, {
  message: 'You must accept the Terms of Service and Privacy Policy',
  path: ['consent_terms'],
}).refine((data) => data.consent_data_processing === true, {
  message: 'Data processing consent is required to use this platform',
  path: ['consent_data_processing'],
});

type RegisterFormData = z.infer<typeof registerSchema>;

const RegisterPlayerPage = () => {
  const navigate = useNavigate();
  const { login } = useAuthStore();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const [isBelow18, setIsBelow18] = useState(false);
  const [consentTerms, setConsentTerms] = useState(false);
  const [consentData, setConsentData] = useState(false);
  const [consentMarketing, setConsentMarketing] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    reset,
    setValue,
    formState: { errors },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      is_below_18: false,
      consent_terms: false,
      consent_data_processing: false,
      consent_marketing: false,
    },
  });

  const handleRegistrationTypeChange = (event: React.MouseEvent<HTMLElement>, newValue: boolean | null) => {
    if (newValue !== null) {
      setIsBelow18(newValue);
      setValue('is_below_18', newValue);
      setConsentTerms(false);
      setConsentData(false);
      setConsentMarketing(false);
      reset({
        is_below_18: newValue,
        first_name: '',
        last_name: '',
        email: '',
        password: '',
        date_of_birth: '',
        gender: undefined,
        phone: '',
        parent_email: '',
        parent_phone: '',
        parent_country: '',
        father_name: '',
        mother_name: '',
        guardian_name: '',
        consent_terms: false,
        consent_data_processing: false,
        consent_marketing: false,
      });
    }
  };

  const onSubmit = async (data: RegisterFormData) => {
    setIsLoading(true);
    setError('');

    try {
      // Prepare data for API
      const apiData: any = {
        email: isBelow18 ? data.parent_email : data.email,
        password: data.password,
        first_name: data.first_name,
        last_name: data.last_name || '',
        date_of_birth: data.date_of_birth,
        gender: data.gender,
        is_below_18: isBelow18,
      };

      // Add parent fields if below 18
      if (isBelow18) {
        apiData.parent_email = data.parent_email;
        apiData.parent_phone = data.parent_phone;
        apiData.parent_country = data.parent_country;
        apiData.father_name = data.father_name;
        apiData.mother_name = data.mother_name;
      } else {
        // For regular players, use their own email
        apiData.email = data.email;
        if (data.phone) {
          apiData.phone = data.phone;
        }
      }

      apiData.consent_data_processing = consentData;
      apiData.consent_marketing = consentMarketing;
      apiData.consent_terms = consentTerms;
      if (isBelow18) {
        apiData.guardian_name = data.guardian_name || data.father_name || '';
        apiData.guardian_email = data.parent_email;
      }

      const response = await authService.registerPlayer(apiData);
      login(response.user);
      toast.success('Registration successful!');
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container maxWidth="md">
      <Box sx={{ marginTop: 4, marginBottom: 4 }}>
        <Paper elevation={3} sx={{ padding: 4 }}>
          <Typography component="h1" variant="h4" align="center" gutterBottom>
            Player Registration
          </Typography>
          <Typography variant="body1" align="center" color="textSecondary" gutterBottom>
            Create your player account to start tracking your performance
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ mt: 3 }}>
            {/* Registration Type Toggle - At the Top */}
            <Grid item xs={12} sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <ToggleButtonGroup
                  value={isBelow18}
                  exclusive
                  onChange={handleRegistrationTypeChange}
                  aria-label="registration type"
                  sx={{
                    '& .MuiToggleButton-root': {
                      px: 4,
                      py: 1.5,
                      fontSize: '1rem',
                    },
                  }}
                >
                  <ToggleButton value={false} aria-label="regular player">
                    Regular Player (18+)
                  </ToggleButton>
                  <ToggleButton value={true} aria-label="below 18">
                    Player Below 18
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
              <Typography variant="body2" color="textSecondary" align="center">
                {isBelow18 
                  ? 'Registering for a player below 18 years. Parent/Guardian information required.'
                  : 'Registering as a regular player (18 years or above).'}
              </Typography>
            </Grid>

            <Divider sx={{ my: 2 }} />

            <Grid container spacing={2}>
              {isBelow18 ? (
                <>
                  {/* Below 18 Registration Form */}
                  <Grid item xs={12}>
                    <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
                      Player/Child Information
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      {...register('first_name')}
                      label="Name of your Child/Player *"
                      fullWidth
                      error={!!errors.first_name}
                      helperText={errors.first_name?.message}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      {...register('last_name')}
                      label="Last Name (Optional)"
                      fullWidth
                      error={!!errors.last_name}
                      helperText={errors.last_name?.message}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      {...register('date_of_birth')}
                      label="Date of Birth *"
                      type="date"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      error={!!errors.date_of_birth}
                      helperText={errors.date_of_birth?.message}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="gender"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          select
                          label="Gender *"
                          fullWidth
                          error={!!errors.gender}
                          helperText={errors.gender?.message}
                        >
                          <MenuItem value={GenderType.MALE}>Male</MenuItem>
                          <MenuItem value={GenderType.FEMALE}>Female</MenuItem>
                          <MenuItem value={GenderType.OTHER}>Other</MenuItem>
                        </TextField>
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sx={{ mt: 2 }}>
                    <Divider />
                    <Typography variant="h6" sx={{ mt: 2, mb: 2, color: 'primary.main' }}>
                      Parent/Guardian Information
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      {...register('parent_email')}
                      label="Parent Email Address *"
                      type="email"
                      fullWidth
                      error={!!errors.parent_email}
                      helperText={errors.parent_email?.message}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      {...register('parent_phone')}
                      label="Parent Phone Number *"
                      fullWidth
                      error={!!errors.parent_phone}
                      helperText={errors.parent_phone?.message}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      {...register('parent_country')}
                      label="Country *"
                      fullWidth
                      error={!!errors.parent_country}
                      helperText={errors.parent_country?.message}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      {...register('father_name')}
                      label="Father Name *"
                      fullWidth
                      error={!!errors.father_name}
                      helperText={errors.father_name?.message}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      {...register('mother_name')}
                      label="Mother Name *"
                      fullWidth
                      error={!!errors.mother_name}
                      helperText={errors.mother_name?.message}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      {...register('guardian_name')}
                      label="Guardian Full Name (Legal Name) *"
                      fullWidth
                      error={!!errors.guardian_name}
                      helperText={errors.guardian_name?.message || 'Your full legal name as the consenting parent/guardian'}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      {...register('password')}
                      label="Password *"
                      type="password"
                      fullWidth
                      error={!!errors.password}
                      helperText={errors.password?.message}
                    />
                  </Grid>
                </>
              ) : (
                <>
                  {/* Regular Player Registration Form (18+) */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      {...register('first_name')}
                      label="First Name *"
                      fullWidth
                      error={!!errors.first_name}
                      helperText={errors.first_name?.message}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      {...register('last_name')}
                      label="Last Name *"
                      fullWidth
                      error={!!errors.last_name}
                      helperText={errors.last_name?.message}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      {...register('email')}
                      label="Email Address *"
                      type="email"
                      fullWidth
                      autoComplete="email"
                      error={!!errors.email}
                      helperText={errors.email?.message}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      {...register('password')}
                      label="Password *"
                      type="password"
                      fullWidth
                      error={!!errors.password}
                      helperText={errors.password?.message}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      {...register('date_of_birth')}
                      label="Date of Birth *"
                      type="date"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      error={!!errors.date_of_birth}
                      helperText={errors.date_of_birth?.message}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="gender"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          select
                          label="Gender *"
                          fullWidth
                          error={!!errors.gender}
                          helperText={errors.gender?.message}
                        >
                          <MenuItem value={GenderType.MALE}>Male</MenuItem>
                          <MenuItem value={GenderType.FEMALE}>Female</MenuItem>
                          <MenuItem value={GenderType.OTHER}>Other</MenuItem>
                        </TextField>
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      {...register('phone')}
                      label="Phone (Optional)"
                      fullWidth
                      error={!!errors.phone}
                      helperText={errors.phone?.message}
                    />
                  </Grid>
                </>
              )}
            </Grid>

            {/* ── Consent & Legal ── */}
            <Divider sx={{ my: 3 }} />
            {isBelow18 ? (
              <Box sx={{ mb: 2 }}>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                    Parental / Guardian Consent Required (COPPA · GDPR-K)
                  </Typography>
                  You are registering a player under 18. By checking the boxes below you confirm that
                  you are the parent or legal guardian and you give informed consent for your child's
                  personal data to be processed on this platform. This record is stored with a
                  timestamp and IP address as required by law.
                </Alert>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={consentData}
                      onChange={(e) => {
                        setConsentData(e.target.checked);
                        setValue('consent_data_processing', e.target.checked);
                      }}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body2">
                      <strong>Required:</strong> I am the parent/legal guardian of the child named above and I
                      consent to the collection and processing of their personal data (name, date of birth,
                      performance data, contest videos) for the purposes of performance tracking, contest
                      participation, and coaching services as described in the{' '}
                      <Link component={RouterLink} to="/privacy-policy" target="_blank">Privacy Policy</Link>.
                    </Typography>
                  }
                  sx={{ alignItems: 'flex-start', mb: 1 }}
                />
                {errors.consent_data_processing && (
                  <Alert severity="error" sx={{ mb: 1 }}>{errors.consent_data_processing.message}</Alert>
                )}
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={consentTerms}
                      onChange={(e) => {
                        setConsentTerms(e.target.checked);
                        setValue('consent_terms', e.target.checked);
                      }}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body2">
                      <strong>Required:</strong> I accept the{' '}
                      <Link component={RouterLink} to="/privacy-policy" target="_blank">Terms of Service and Privacy Policy</Link>{' '}
                      on behalf of my child.
                    </Typography>
                  }
                  sx={{ alignItems: 'flex-start', mb: 1 }}
                />
                {errors.consent_terms && (
                  <Alert severity="error" sx={{ mb: 1 }}>{errors.consent_terms.message}</Alert>
                )}
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={consentMarketing}
                      onChange={(e) => {
                        setConsentMarketing(e.target.checked);
                        setValue('consent_marketing', e.target.checked);
                      }}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body2">
                      <em>Optional:</em> I consent to receiving performance updates and news about contests via the email provided above.
                    </Typography>
                  }
                  sx={{ alignItems: 'flex-start' }}
                />
              </Box>
            ) : (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
                  Consent &amp; Legal (GDPR)
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={consentData}
                      onChange={(e) => {
                        setConsentData(e.target.checked);
                        setValue('consent_data_processing', e.target.checked);
                      }}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body2">
                      <strong>Required:</strong> I consent to my personal data (name, date of birth, performance data,
                      contest videos) being processed for performance tracking, contest participation, and coaching
                      services as described in the{' '}
                      <Link component={RouterLink} to="/privacy-policy" target="_blank">Privacy Policy</Link>.
                    </Typography>
                  }
                  sx={{ alignItems: 'flex-start', mb: 1 }}
                />
                {errors.consent_data_processing && (
                  <Alert severity="error" sx={{ mb: 1 }}>{errors.consent_data_processing.message}</Alert>
                )}
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={consentTerms}
                      onChange={(e) => {
                        setConsentTerms(e.target.checked);
                        setValue('consent_terms', e.target.checked);
                      }}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body2">
                      <strong>Required:</strong> I accept the{' '}
                      <Link component={RouterLink} to="/privacy-policy" target="_blank">Terms of Service and Privacy Policy</Link>.
                    </Typography>
                  }
                  sx={{ alignItems: 'flex-start', mb: 1 }}
                />
                {errors.consent_terms && (
                  <Alert severity="error" sx={{ mb: 1 }}>{errors.consent_terms.message}</Alert>
                )}
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={consentMarketing}
                      onChange={(e) => {
                        setConsentMarketing(e.target.checked);
                        setValue('consent_marketing', e.target.checked);
                      }}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body2">
                      <em>Optional:</em> I agree to receive occasional performance tips, contest announcements, and platform news.
                    </Typography>
                  }
                  sx={{ alignItems: 'flex-start' }}
                />
              </Box>
            )}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              sx={{ mt: 1, mb: 2 }}
              disabled={isLoading || !consentData || !consentTerms}
            >
              {isLoading ? 'Registering...' : 'Register'}
            </Button>

            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="textSecondary">
                Already have an account?{' '}
                <Link component={RouterLink} to="/login">
                  Sign In
                </Link>
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default RegisterPlayerPage;


